// Copyright (c) StrangeBee 2023

import { ItemProps } from '@Molecules/THForm/Item';
import { THForm, THFormProps } from '@Molecules/THForm/THForm';
import { Form } from 'antd';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { THEditableButtons } from '@Molecules/Inputs/THEditableButtons/THEditableButtons';

import './THEditable.scss';

interface THEditableProps<T> {
    children: ReactElement;
    initialValues?: Partial<T>;
    onFinish?: (values: T) => void;
    name: string;
    label?: string;
    horizontal?: boolean;
    submitOnChange?: boolean;
    formName?: string;
    formItemProps?: ItemProps<T>;
    formProps?: THFormProps<T>;
    className?: string;
    withDrawer?: boolean;
}

export const THEditable = <T,>({
    children,
    initialValues,
    onFinish,
    name,
    label,
    horizontal,
    formProps,
    formItemProps,
    submitOnChange,
    className,
    withDrawer = false,
}: THEditableProps<T>): ReactElement => {
    const [form] = Form.useForm();
    const hiddenRef = useRef<any>(null);
    const [visible, setVisible] = useState(false);

    const Buttons = !submitOnChange && visible && !withDrawer && (
        <THEditableButtons
            onCancel={() => {
                form.resetFields();
                setVisible(false);
            }}
        />
    );

    useEffect(() => {
        form.resetFields();
    }, [form, initialValues]);

    return (
        <THForm
            editable
            form={form}
            className={className}
            onFinish={(values) => {
                onFinish?.(values);
                setVisible(false);
                hiddenRef.current?.focus();
            }}
            initialValues={initialValues}
            onValuesChange={() => {
                if (withDrawer) {
                    //Prevent change selected value before validation
                    form.resetFields();
                } else {
                    submitOnChange && form.submit();
                }
                setVisible(true);
            }}
            {...formProps}
        >
            <div className="th-editable__layout">
                <THForm.Item<T>
                    name={name}
                    label={label}
                    className="th-editable__form-item"
                    extraAction={!horizontal && Buttons}
                    {...formItemProps}
                >
                    {children}
                </THForm.Item>

                {horizontal && Buttons}
            </div>
        </THForm>
    );
};
